import React from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    IconButton,
    Menu,
    MenuItem,
    useMediaQuery,
    useTheme,
    Container,
    Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

import trendLogo from '../../assets/images/logo.png';
import { motion } from 'framer-motion';
import GoogleTranslateBox from './GoogleTranslateBox';
import './googleTranslate.css';
import ThemeToggle from '../ThemeToggle';

const Navbar = ({
    onConnect,
    isConnected,
    account,
    activeButton,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Navigation Items
    const navItems = [
        { label: 'Benefits', id: 'benefits' },
        { label: 'Features', id: 'features' }, // Assuming features maps to HowItWorks or Stats? Let's use HowItWorks for now or separate
        { label: 'How it works', id: 'how-it-works' },
        { label: 'Explore', id: 'products' }, // Explore
    ];

    const handleScrollTo = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Mobile Nav State
    const [anchorElNav, setAnchorElNav] = React.useState(null);

    const handleOpenNavMenu = (event) => {
        setAnchorElNav(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };


    return (
        <AppBar
            position="fixed"
            sx={{
                background: theme.palette.background.paper,
                backdropFilter: 'blur(40px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 4px 30px rgba(0,0,0,0.5)' : '0 2px 20px rgba(0,0,0,0.1)',
                borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)',
                borderTop: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1100,
            }}
        >
            <Container maxWidth="xl">
                <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: '80px' }}>
                    {/* Logo Area */}
                    <Box
                        component={motion.img}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        src={trendLogo}
                        alt="Trend Ads"
                        sx={{
                            height: { xs: 30, md: 40 },
                            cursor: 'pointer',
                            filter: 'hue-rotate(40deg) drop-shadow(0 0 2px rgba(0, 240, 255, 0.5))', // Shift Green to Blue
                        }}
                        onClick={() => window.location.href = '/'}
                    />

                    {/* Desktop Menu */}
                    {!isMobile && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {navItems.map((item) => (
                                <Button
                                    key={item.label}
                                    onClick={() => handleScrollTo(item.id)}
                                    component={motion.button}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    sx={{
                                        my: 2,
                                        color: theme.palette.text.secondary,
                                        display: 'block',
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        '&:hover': {
                                            color: theme.palette.primary.main,
                                            background: 'transparent',
                                        },
                                    }}
                                >
                                    {item.label}
                                </Button>
                            ))}
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ThemeToggle />
                            </Box>
                            <Box
                                sx={{
                                    borderRadius: '50px',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid',
                                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s',
                                    height: '38px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 0.5,
                                    '&:hover': {
                                        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                        borderColor: theme.palette.primary.main
                                    }
                                }}
                            >
                                <GoogleTranslateBox />
                            </Box>
                        </Box>
                    )}

                    {/* Mobile Menu Icon */}
                    {isMobile && (
                        <Box>
                            <IconButton
                                size="large"
                                aria-label="menu"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleOpenNavMenu}
                                sx={{ color: theme.palette.text.primary }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorElNav}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                open={Boolean(anchorElNav)}
                                onClose={handleCloseNavMenu}
                                sx={{
                                    display: { xs: 'block', md: 'none' },
                                    '& .MuiPaper-root': {
                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(20,20,20,0.95)' : 'rgba(255,255,255,0.95)',
                                        backdropFilter: 'blur(20px)',
                                        border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                                        minWidth: '200px',
                                    }
                                }}
                            >
                                {navItems.map((item) => (
                                    <MenuItem key={item.label} onClick={() => { handleScrollTo(item.id); handleCloseNavMenu(); }}>
                                        <Typography textAlign="center" sx={{ color: theme.palette.text.primary }}>{item.label}</Typography>
                                    </MenuItem>
                                ))}
                                <MenuItem disableRipple onKeyDown={(e) => e.stopPropagation()}>
                                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        <Box
                                            sx={{
                                                borderRadius: '12px',
                                                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                                backdropFilter: 'blur(10px)',
                                                border: '1px solid',
                                                borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                                width: '100%',
                                                px: 1,
                                                py: 0.5
                                            }}
                                        >
                                            <GoogleTranslateBox fullWidth />
                                        </Box>
                                    </Box>
                                </MenuItem>
                                <MenuItem disableRipple onKeyDown={(e) => e.stopPropagation()}>
                                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        <ThemeToggle />
                                    </Box>
                                </MenuItem>

                            </Menu>
                        </Box>
                    )}
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export default Navbar;
